import React, { useState, useMemo, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useGetOrganizationsQuery } from '../../../features/organizations/organizationsApiSlice';
import { useGetDataAssetsQuery } from '../../../features/data_assets/dataAssetsApiSlice';
import { Switch, Select, MenuItem, List, ListItem, Button, Dialog, DialogActions, DialogContent, DialogTitle, Box, Typography, CircularProgress } from '@mui/material';
import Header from '../global/Header';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';


const DeliveryMatrix = () => {
    // Assume `useGetOrganizationsQuery` and `useGetDataAssetsQuery` hooks are correctly fetching data
    const { data: organizationsResponse, isLoading: orgLoading } = useGetOrganizationsQuery('organizations');
    const { data: dataAssetsResponse, isLoading: assetsLoading } = useGetDataAssetsQuery('dataAssets');

    const [rows, setRows] = useState([]);

    const [changes, setChanges] = useState({});
    const [openDialog, setOpenDialog] = useState(false);

    // Convert entities object to an array of data assets and organizations
    const safeDataAssets = useMemo(() => Object.values(dataAssetsResponse?.entities || {}), [dataAssetsResponse]);
    const safeOrganizations = useMemo(() => Object.values(organizationsResponse?.entities || {}), [organizationsResponse]);

    // Filtering external data assets outside of the useMemo for columns to make it accessible for rows calculation
    const externalDataAssets = safeDataAssets.filter(asset => asset.usage === "External");

    const handleCellEditCommit = React.useCallback((params) => {
        // Update the rows state to reflect the change
        setRows((prevRows) => {
            const newRows = prevRows.map(row => {
                if (row.id === params.id) {
                    return { ...row, [params.field]: params.value === 'true' };
                }
                return row;
            });
            return newRows;
        });
        // Optionally accumulate changes for later saving
        setChanges((prevChanges) => ({
            ...prevChanges,
            [params.id]: {
                ...prevChanges[params.id],
                [params.field]: params.value,
            },
        }));
    }, []);
    const BooleanEditInputCell = (params) => {
        const handleChange = (event) => {
            // Update the local changes state
            params.api.updateRows([{ id: params.id, [params.field]: event.target.value === 'true' }]);

            // Optionally, if you manage local changes before saving, update your local state here
        };

        return (
            <Select
                value={params.value ? 'true' : 'false'}
                onChange={handleChange}
                size="small"
                fullWidth
            >
                <MenuItem value="true">True</MenuItem>
                <MenuItem value="false">False</MenuItem>
            </Select>
        );
    };

    const columns = useMemo(() => {
        const baseColumns = [
            { field: 'name', headerName: 'Organization Name', width: 200 },
            { field: 'bucketLocation', headerName: 'Bucket Location', width: 150 },
            // Other base columns...
        ];

        const dynamicColumns = externalDataAssets.map(asset => ({
            field: asset.name,
            headerName: asset.name,
            width: 150,
            editable: true, // Make the cell editable
            renderCell: (params) => (
                <span style={{ fontWeight: 'bold', fontSize: '1.5em', color: params.value ? 'green' : 'red' }}>
                    {params.value ? '✓' : '✗'}
                </span>
            ),
            renderEditCell: BooleanEditInputCell, // Use the custom edit component
        }));

        return [...baseColumns, ...dynamicColumns];
    }, [externalDataAssets]);

    useEffect(() => {
        const calculatedRows = safeOrganizations.filter(org => org.s3_config?.infra_data?.onprem_config?.onprem_bucket)
            .map((org, index) => ({
                id: index,
                name: org.name,
                bucketLocation: org.s3_config?.infra_data?.onprem_config?.bucket_location || 'N/A',
                // Add additional properties for each external data asset
                // This might involve setting initial boolean values or other relevant data
            }));

        // Now set the rows state with the newly calculated rows
        setRows(calculatedRows);
    }, [safeOrganizations, externalDataAssets]); // Ensure externalDataAssets is included here as well

    if (orgLoading || assetsLoading) {
        return <Box>Loading...</Box>;
    }



    // Assume an update function is available
    //const [updateOrganization, { isLoading: isUpdating }] = useUpdateOrganizationMutation();



    const handleConfirmChanges = async () => {
        // Logic to apply changes: for simplicity, just console.log here
        console.log(changes);
        setOpenDialog(false);
        setChanges({});
        // Invoke your update function here with the necessary parameters
    };

    const handleCancelChanges = () => {
        setOpenDialog(false);
        setChanges({});
    };





    return (
        <Box m={2}>
            <Header title="LDC Info" />
            <Box display="flex" justifyContent="end" mb={2}>
                <Button variant="contained" color="primary" onClick={() => setOpenDialog(true)}>
                    Save Changes
                </Button>
            </Box>
            <DataGridPro
                rows={rows}
                columns={columns}
                components={{ Toolbar: GridToolbar }}
                onCellEditCommit={handleCellEditCommit}
            // Configure other necessary DataGridPro props
            />
            <Dialog open={openDialog} onClose={handleCancelChanges}>
                <DialogTitle>Confirm Changes</DialogTitle>
                <DialogContent>
                    {/* Display changes here */}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelChanges}>Cancel</Button>
                    <Button onClick={handleConfirmChanges} color="primary">Confirm</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DeliveryMatrix;